<template>
  <v-container fluid dense>
    <v-alert
      v-if="SHOW_ALERT"
      prominent
      type="error">
      <v-row align="center">
        <v-col class="grow">{{ ALERT_TEXT }}</v-col>
        <v-col class="shrink">
          <v-btn @click="accept()">Ok</v-btn>
        </v-col>
      </v-row>
    </v-alert>
    <v-dialog
      v-model="confirmDirtyDialogVisible"
      max-width="500px">
      <v-card>
        <v-card-title>
          The Run Sheet contains unsaved data.  Continue anyway?
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="cancelDirtyForm">Cancel</v-btn>
          <v-btn color="blue darken-1" text @click="proceedDirtyForm">OK</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-row>
      <v-col lg="2" sm="6" md="4" class="pb-1">
        <v-menu
          v-model="menu"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="290px">
          <template v-slot:activator="{ on }">
            <v-text-field
              v-model="fmtBookingDate"
              label="Booking date"
              prepend-icon="mdi-calendar"
              readonly
              v-on="on">
            </v-text-field>
          </template>
          <v-date-picker
            v-model="bookingDate"
            @input="menu = false">
          </v-date-picker>
        </v-menu>
      </v-col>
      <v-col class="text-right">
        <v-btn
          v-if="!isDriverView"
          color="blue-grey"
          class="ma-2 white--text"
          @click="toggleView(true)">
          Driver View
          <v-icon right dark>
            mdi-view-headline
          </v-icon>
        </v-btn>
        <v-btn
          v-if="!!isDriverView"
          color="blue-grey"
          class="ma-2 white--text"
          @click="toggleView(false)">
          Standard View
          <v-icon right dark>
            mdi-view-dashboard
          </v-icon>
        </v-btn>
        <v-dialog
          v-model="setupBusesDialog"
          persistent
          scrollable
          max-width="600px">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="IS_ADMINISTRATOR || IS_SYSTEM_ADMINISTRATOR"
              color="blue-grey"
              class="ma-2 white--text"
              @click="setupBuses()">
              Setup Buses
              <v-icon right dark>
                mdi-bus-alert
              </v-icon>
            </v-btn>
          </template>
          <v-card width="100vw" class="busSetup">
            <v-card-title>
              <span class="headline">Manage Buses</span>
            </v-card-title>
            <v-card-text style="height: 600px;">
              <v-data-table
                :headers="busSetupHeaders"
                :items="busSetupItems"
                :hide-default-footer="true"
                :items-per-page="-1"
                class="elevation-1"
                dense>
                <template v-slot:item.dayDriverId="{ item }">
                  <v-select
                    class="selectBus"
                    v-model="item.dayDriverId"
                    :items="driverList"
                    item-text="driverName"
                    item-value="driverId"
                    label="Select"
                    persistent-hint
                    single-line
                    dense
                    hide-details="auto">
                  </v-select>
                </template>
                <template v-slot:item.nightDriverId="{ item }">
                  <v-select
                    class="selectBus"
                    v-model="item.nightDriverId"
                    :items="driverList"
                    item-text="driverName"
                    item-value="driverId"
                    label="Select"
                    persistent-hint
                    single-line
                    dense
                    hide-details="auto">
                  </v-select>
                </template>
              </v-data-table>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="blue darken-1" text @click="closeAssignList()">
                Close
              </v-btn>
              <v-btn color="blue darken-1" text @click="saveAssignList()">
                Save
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
    <v-row :class="this.dirty ? 'dirty-form' : ''">
      <splitpanes class="default-theme" :horizontal="isDriverView">
        <pane class="departures-pane">
          <v-card>
             <v-card-title>Departures</v-card-title>
          </v-card>
          <bus-run-control
            directionDescription="Departures"
            :directionType="toAirportDirectionType"
            :pickupTimes="pickupTimes"
            :busRuns="toAirport"
            :busList="busList"
            :bookingDate="fmtBookingDate"
            :isDriverView="!!isDriverView">
          </bus-run-control>
        </pane>
        <pane>
          <v-card>
             <v-card-title>Arrivals</v-card-title>
          </v-card>
          <bus-run-control
            directionDescription="Arrivals"
            :directionType="fromAirportDirectionType"
            :busRuns="fromAirport"
            :busList="busList"
            :bookingDate="fmtBookingDate"
            :isDriverView="!!isDriverView">
          </bus-run-control>
        </pane>
      </splitpanes>
    </v-row>

    <v-btn
      bottom
      elevated
      fixed
      left
      color="primary"
      @click="saveRunDetails()">
        Save Run Details
        <v-icon dark>
          mdi-bus-alert
        </v-icon>
      </v-btn>

  </v-container>
</template>

<script>
import 'splitpanes/dist/splitpanes.css';
import { Splitpanes, Pane } from 'splitpanes';
import { mapGetters, mapActions } from 'vuex';
import storeConstants from '@/modules/bus-run-manager/store/storeConstants';
import rootStoreConstants from '@/store/storeConstants';
import BusRunControl from '@/modules/bus-run-manager/components/BusRunControl.vue';

export default {
  name: 'BusRunManager',
  components: {
    'bus-run-control': BusRunControl,
    Splitpanes,
    Pane,
  },
  data: () => ({
    splitPaneType: 'vertical',
    isDriverView: false,
    menu: false,
    dirty: false,
    confirmDirtyDialogVisible: false,
    confirmDirtyDialog: undefined,
    setupBusesDialog: false,
    bookingDate: null,
    toAirportDirectionType: 1,
    fromAirportDirectionType: 2,
    busSetupHeaders: [
      {
        text: 'Bus',
        align: 'left',
        sortable: false,
        value: 'busName',
        width: 100,
      },
      {
        text: 'Day Driver',
        align: 'center',
        sortable: false,
        value: 'dayDriverId',
      },
      {
        text: 'Night Driver',
        align: 'center',
        sortable: false,
        value: 'nightDriverId',
      }],
  }),
  methods: {
    ...mapActions('busRunManagerModule', [
      storeConstants.GET_BUS_SUMMARY,
      storeConstants.GET_ASSIGNED_BUSES,
      storeConstants.SAVE_BUS_SETUP,
      storeConstants.SAVE_RUN_DETAILS]),
    ...mapActions([rootStoreConstants.SET_ALERT_DETAILS]),
    accept() {
      this.SET_ALERT_DETAILS({ alertText: undefined, showAlert: false });
    },
    setupBuses() {
      this.$store.commit(rootStoreConstants.SET_LOADING_TEXT, 'Retrieving assigned buses...');
      this.$store.commit(rootStoreConstants.SET_LOADING, true);

      this.GET_ASSIGNED_BUSES(this.bookingDate).then(() => {
        this.$store.commit(rootStoreConstants.SET_LOADING_TEXT, 'Retrieving assigned buses...');
        this.$store.commit(rootStoreConstants.SET_LOADING, false);
        this.setupBusesDialog = true;
      },
      (error) => {
        this.SET_ALERT_DETAILS({ alertText: error, showAlert: true });
        this.$store.commit(rootStoreConstants.SET_LOADING, false);
      }).catch(() => {
        this.$store.commit(rootStoreConstants.SET_LOADING, false);
      });
    },
    saveRunDetails() {
      this.SAVE_RUN_DETAILS(
        {
          bookingDate: this.bookingDate,
          toAirport: this.toAirport,
          fromAirport: this.fromAirport,
        },
      ).then(() => {
        this.GET_BUS_SUMMARY(this.bookingDate)
          .then(() => {
            this.$store.commit(rootStoreConstants.SET_LOADING, false);
            this.setupBusesDialog = false;
            this.dirty = false;
            this.confirmDirtyDialogVisible = false;
          }, (busSummaryerror) => {
            this.SET_ALERT_DETAILS({ alertText: busSummaryerror, showAlert: true });
            this.$store.commit(rootStoreConstants.SET_LOADING, false);
          }).catch(() => {
            this.$store.commit(rootStoreConstants.SET_LOADING, false);
          });
      }, (error) => {
        this.SET_ALERT_DETAILS({ alertText: error, showAlert: true });
        this.$store.commit(rootStoreConstants.SET_LOADING, false);
      }).catch(() => {
        this.$store.commit(rootStoreConstants.SET_LOADING, false);
      });
    },
    saveAssignList() {
      this.$store.commit(rootStoreConstants.SET_LOADING_TEXT, 'Saving bus setup...', { root: true });
      this.$store.commit(rootStoreConstants.SET_LOADING, true, { root: true });

      this.SAVE_BUS_SETUP({ bookingDate: this.bookingDate, items: this.busSetupItems })
        .then(() => {
          this.GET_BUS_SUMMARY(this.bookingDate)
            .then(() => {
              this.$store.commit(rootStoreConstants.SET_LOADING, false);
              this.setupBusesDialog = false;
            }, (busSummaryError) => {
              this.SET_ALERT_DETAILS({
                alertText: !!busSummaryError.response && !!busSummaryError.response.data
                  ? busSummaryError.response.data.error : busSummaryError,
                showAlert: true,
              });
              this.$store.commit(rootStoreConstants.SET_LOADING, false);
            });
        }, (error) => {
          this.SET_ALERT_DETAILS({
            alertText: !!error.response && !!error.response.data
              ? error.response.data.error : error,
            showAlert: true,
          });
          this.$store.commit(rootStoreConstants.SET_LOADING, false);
        })
        .catch(() => {
          this.$store.commit(rootStoreConstants.SET_LOADING, false);
        });
    },
    closeAssignList() {
      this.setupBusesDialog = false;
    },
    proceedDirtyForm() {
      this.confirmDirtyDialog.resolve();
    },
    cancelDirtyForm() {
      this.confirmDirtyDialog.reject();
    },
    showDirtyFormDialog() {
      this.confirmDirtyDialogVisible = true;
      return new Promise((resolve, rej) => {
        const reject = () => {
          this.confirmDirtyDialogVisible = false;
          rej();
        };
        this.confirmDirtyDialog = { resolve, reject };
      });
    },
    getBusSummary(date) {
      this.GET_BUS_SUMMARY(date)
        .then(() => {
          this.$store.commit(rootStoreConstants.SET_LOADING, false);
          this.setupBusesDialog = false;
          this.dirty = false;
          this.confirmDirtyDialogVisible = false;
        }, (error) => {
          this.SET_ALERT_DETAILS({
            alertText: !!error.response && !!error.response.data
              ? error.response.data.error : error,
            showAlert: true,
          });
          this.$store.commit(rootStoreConstants.SET_LOADING, false);
        });
    },
    toggleView(showDriverView) {
      this.isDriverView = showDriverView;
      this.splitPaneType = !showDriverView ? 'vertical' : 'horizontal';
    },
  },
  created() {
    this.bookingDate = this.$moment().format(this.ISO_8601_FORMAT);
  },
  beforeRouteLeave(to, from, next) {
    if (this.dirty) {
      this.showDirtyFormDialog()
        .then(next)
        .catch(() => next(false));
    }
    next();
  },
  computed: {
    ...mapGetters('busRunManagerModule', [
      storeConstants.BUS_SUMMARY,
      storeConstants.ASSIGNED_BUSES]),
    ...mapGetters([
      rootStoreConstants.IS_ADMINISTRATOR,
      rootStoreConstants.IS_SYSTEM_ADMINISTRATOR,
      rootStoreConstants.DISPLAY_DATE_FORMAT,
      rootStoreConstants.ISO_8601_FORMAT,
      rootStoreConstants.SHOW_ALERT,
      rootStoreConstants.ALERT_TEXT]),
    fmtISOBookingDate() {
      if (!this.bookingDate) {
        return null;
      }
      return this.$moment(this.bookingDate).add(this.$moment().utcOffset(), 'minutes').toDate();
    },
    fmtBookingDate() {
      if (!this.bookingDate) {
        return null;
      }
      return this.$moment(this.bookingDate).format(this.DISPLAY_DATE_FORMAT);
    },
    pickupTimes() {
      const summary = this.BUS_SUMMARY;
      return summary.pickupTimes;
    },
    toAirport() {
      const summary = this.BUS_SUMMARY;
      return summary.toAirport;
    },
    fromAirport() {
      const summary = this.BUS_SUMMARY;
      return summary.fromAirport;
    },
    busList() {
      const summary = this.BUS_SUMMARY;
      return summary.busList;
    },
    busSetupItems() {
      const busSetupItems = this.ASSIGNED_BUSES;
      return busSetupItems.assignedBuses;
    },
    driverList() {
      const busSetupItems = this.ASSIGNED_BUSES;
      return busSetupItems.drivers;
    },
  },
  watch: {
    toAirport:
    {
      handler() {
        this.dirty = true;
      },
      deep: true,
    },
    fromAirport:
    {
      handler() {
        this.dirty = true;
      },
      deep: true,
    },
    bookingDate(val) {
      if (this.dirty) {
        this.showDirtyFormDialog()
          .then(() => {
            this.getBusSummary(val);
          })
          .catch(() => null);
      } else {
        this.getBusSummary(val);
      }
    },
  },
};
</script>
<style>
.departures-pane {
  margin-left: 1rem;
}
.busSetup .v-input.selectBus {
  font-size: 0.7rem;
}
.busSetupDialog {
  background-color: #4682b4;
}
.dirty-form {
  border: 1px solid #ff0000;
}
</style>
